
import React, { useState, useRef, useEffect } from 'react';
import { Table, TableShape, DragState, Zone, ZoneType, FloorDimensions, CategoryDefinition, Reservation, ArenaConfig } from '../types';
import { TableNode } from './TableNode';
import { ZoneNode } from './ZoneNode';
import { Layout, LayoutGrid, BoxSelect, Maximize, Palette, Triangle, Tag, RotateCw, Trash2, Plus, Minus, Pencil, Check, Radio, RotateCcw } from 'lucide-react';

interface FloorPlanEditorProps {
  tables: Table[];
  onUpdateTables: (tables: Table[]) => void;
  zones: Zone[];
  onUpdateZones: (zones: Zone[]) => void;
  dimensions: FloorDimensions;
  onUpdateDimensions: (dim: FloorDimensions) => void;
  categories: CategoryDefinition[];
  reservations: Reservation[]; 
  config: ArenaConfig; 
  onReset?: () => void; 
  onTableClick?: (tableId: string) => void;
}

export const FloorPlanEditor: React.FC<FloorPlanEditorProps> = ({ 
  tables, 
  onUpdateTables,
  zones, 
  onUpdateZones,
  dimensions,
  onUpdateDimensions,
  categories,
  reservations,
  config,
  onReset,
  onTableClick
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectionType, setSelectionType] = useState<'TABLE' | 'ZONE' | null>(null);
  const [activeTab, setActiveTab] = useState<'FURNITURE' | 'LAYOUT'>('LAYOUT');
  const [currentTime, setCurrentTime] = useState(new Date());

  const isSimple = config.theme?.mode === 'SIMPLE';
  const isDarkMode = config.theme?.darkMode;

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  
  const [clipboard, setClipboard] = useState<{ type: 'TABLE' | 'ZONE', data: Table | Zone } | null>(null);

  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    type: null,
    itemId: null,
    offsetX: 0,
    offsetY: 0,
  });

  const canvasRef = useRef<HTMLDivElement>(null);

  const getRawTableSize = (table: Table) => {
    if (table.width && table.height) {
        return { width: table.width, height: table.height };
    }

    const baseSize = 60;
    let width = baseSize;
    let height = baseSize;

    if (table.shape === TableShape.RECTANGLE) {
      width = baseSize + (table.capacity > 4 ? (table.capacity - 4) * 15 : 0);
      height = baseSize;
    } else if (table.shape === TableShape.CIRCLE) {
      width = baseSize + (table.capacity > 2 ? (table.capacity - 2) * 10 : 0);
      height = width;
    } else if (table.shape === TableShape.TRIANGLE) {
      width = baseSize + (table.capacity > 3 ? (table.capacity - 3) * 15 : 0);
      height = width * 0.866;
    }
    return { width, height };
  };

  const getRotatedBoundingBox = (width: number, height: number, rotationDeg: number) => {
    const rad = (rotationDeg * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rad));
    const cos = Math.abs(Math.cos(rad));
    
    const boundingW = width * cos + height * sin;
    const boundingH = width * sin + height * cos;
    
    return { boundingW, boundingH };
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isEditing) return; 

      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      const isCtrlOrMeta = e.ctrlKey || e.metaKey;

      if (isCtrlOrMeta && e.key.toLowerCase() === 'c') {
        if (selectedId && selectionType) {
          if (selectionType === 'TABLE') {
            const tableToCopy = tables.find(t => t.id === selectedId);
            if (tableToCopy) {
              setClipboard({ type: 'TABLE', data: tableToCopy });
            }
          } else if (selectionType === 'ZONE') {
            const zoneToCopy = zones.find(z => z.id === selectedId);
            if (zoneToCopy) {
              setClipboard({ type: 'ZONE', data: zoneToCopy });
            }
          }
        }
      }

      if (isCtrlOrMeta && e.key.toLowerCase() === 'v') {
        if (clipboard) {
          e.preventDefault(); 
          const newId = crypto.randomUUID();
          const offset = 20; 

          if (clipboard.type === 'TABLE') {
            const data = clipboard.data as Table;
            const { width, height } = getRawTableSize(data);
            
            const { boundingW, boundingH } = getRotatedBoundingBox(width, height, data.rotation);
            const diffW = (boundingW - width) / 2;
            const diffH = (boundingH - height) / 2;

            let pasteX = data.x + offset;
            let pasteY = data.y + offset;

            const minX = diffW;
            const maxX = dimensions.width - width - diffW;
            const minY = diffH;
            const maxY = dimensions.height - height - diffH;

            pasteX = Math.max(minX, Math.min(pasteX, maxX));
            pasteY = Math.max(minY, Math.min(pasteY, maxY));

            const newTable: Table = {
              ...data,
              id: newId,
              name: data.name,
              x: pasteX,
              y: pasteY
            };
            onUpdateTables([...tables, newTable]);
            setSelectedId(newId);
            setSelectionType('TABLE');
          } else if (clipboard.type === 'ZONE') {
            const data = clipboard.data as Zone;
            const newZone: Zone = {
              ...data,
              id: newId,
              name: data.name,
              x: Math.min(data.x + offset, dimensions.width - data.width),
              y: Math.min(data.y + offset, dimensions.height - data.height)
            };
            onUpdateZones([...zones, newZone]);
            setSelectedId(newId);
            setSelectionType('ZONE');
          }
        }
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedId) {
          deleteItem();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, selectionType, clipboard, tables, zones, dimensions, onUpdateTables, onUpdateZones, isEditing]);


  const handleTableMouseDown = (e: React.MouseEvent, tableId: string) => {
    e.stopPropagation();
    const table = tables.find(t => t.id === tableId);
    if (!table || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const offsetX = (e.clientX - canvasRect.left) - table.x;
    const offsetY = (e.clientY - canvasRect.top) - table.y;

    setSelectedId(tableId);
    setSelectionType('TABLE');
    setActiveTab('FURNITURE'); 
    setDragState({
      isDragging: true,
      type: 'TABLE',
      itemId: tableId,
      offsetX,
      offsetY,
    });
  };

  const handleTableResizeStart = (e: React.MouseEvent, tableId: string) => {
    e.stopPropagation();
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    const { width, height } = getRawTableSize(table);

    setSelectedId(tableId);
    setSelectionType('TABLE');
    setDragState({
      isDragging: true,
      type: 'RESIZE_TABLE',
      itemId: tableId,
      offsetX: 0, 
      offsetY: 0,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: width,
      startHeight: height
    });
  };

  const handleZoneMouseDown = (e: React.MouseEvent, zoneId: string) => {
    e.stopPropagation();
    const zone = zones.find(z => z.id === zoneId);
    if (!zone || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const offsetX = (e.clientX - canvasRect.left) - zone.x;
    const offsetY = (e.clientY - canvasRect.top) - zone.y;

    setSelectedId(zoneId);
    setSelectionType('ZONE');
    setActiveTab('LAYOUT'); 
    setDragState({
      isDragging: true,
      type: 'ZONE',
      itemId: zoneId,
      offsetX,
      offsetY,
    });
  };

  const handleZoneResizeStart = (e: React.MouseEvent, zoneId: string) => {
    e.stopPropagation();
    const zone = zones.find(z => z.id === zoneId);
    if (!zone) return;

    setSelectedId(zoneId);
    setSelectionType('ZONE');
    setDragState({
      isDragging: true,
      type: 'RESIZE_ZONE',
      itemId: zoneId,
      offsetX: 0, 
      offsetY: 0,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: zone.width,
      startHeight: zone.height
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragState.isDragging || !dragState.itemId || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const mouseXInCanvas = e.clientX - canvasRect.left;
    const mouseYInCanvas = e.clientY - canvasRect.top;

    if (dragState.type === 'TABLE') {
      const table = tables.find(t => t.id === dragState.itemId);
      if (!table) return;

      let newX = mouseXInCanvas - dragState.offsetX;
      let newY = mouseYInCanvas - dragState.offsetY;
      
      const { width, height } = getRawTableSize(table);
      const { boundingW, boundingH } = getRotatedBoundingBox(width, height, table.rotation);

      newX = Math.round(newX / 2) * 2;
      newY = Math.round(newY / 2) * 2;
      
      const diffW = (boundingW - width) / 2;
      const diffH = (boundingH - height) / 2;

      const minX = diffW;
      const minY = diffH;

      const maxX = dimensions.width - width - diffW;
      const maxY = dimensions.height - height - diffH;

      newX = Math.max(minX, Math.min(newX, maxX));
      newY = Math.max(minY, Math.min(newY, maxY));

      const updatedTables = tables.map(t => 
        t.id === dragState.itemId ? { ...t, x: newX, y: newY } : t
      );
      onUpdateTables(updatedTables);
    } 
    else if (dragState.type === 'ZONE') {
      const zone = zones.find(z => z.id === dragState.itemId);
      if (!zone) return;

      let newX = mouseXInCanvas - dragState.offsetX;
      let newY = mouseYInCanvas - dragState.offsetY;

      newX = Math.round(newX / 2) * 2;
      newY = Math.round(newY / 2) * 2;
      
      newX = Math.max(0, Math.min(newX, dimensions.width - zone.width));
      newY = Math.max(0, Math.min(newY, dimensions.height - zone.height));
      
      const updatedZones = zones.map(z => 
        z.id === dragState.itemId ? { ...z, x: newX, y: newY } : z
      );
      onUpdateZones(updatedZones);
    }
    else if (dragState.type === 'RESIZE_ZONE') {
        const zone = zones.find(z => z.id === dragState.itemId);
        if (!zone) return;

        const deltaX = e.clientX - (dragState.startX || 0);
        const deltaY = e.clientY - (dragState.startY || 0);

        let newWidth = (dragState.startWidth || 100) + deltaX;
        let newHeight = (dragState.startHeight || 100) + deltaY;

        newWidth = Math.max(50, Math.round(newWidth / 2) * 2);
        newHeight = Math.max(50, Math.round(newHeight / 2) * 2);
        
        const maxWidth = dimensions.width - zone.x;
        const maxHeight = dimensions.height - zone.y;

        newWidth = Math.min(newWidth, maxWidth);
        newHeight = Math.min(newHeight, maxHeight);

        const updatedZones = zones.map(z => 
          z.id === dragState.itemId ? { ...z, width: newWidth, height: newHeight } : z
        );
        onUpdateZones(updatedZones);
    }
    else if (dragState.type === 'RESIZE_TABLE') {
        const table = tables.find(t => t.id === dragState.itemId);
        if (!table) return;

        const deltaX = e.clientX - (dragState.startX || 0);
        const deltaY = e.clientY - (dragState.startY || 0);

        let newWidth = (dragState.startWidth || 60) + deltaX;
        let newHeight = (dragState.startHeight || 60) + deltaY;

        newWidth = Math.max(30, Math.round(newWidth / 2) * 2);
        newHeight = Math.max(30, Math.round(newHeight / 2) * 2);

        const updatedTables = tables.map(t => 
          t.id === dragState.itemId ? { ...t, width: newWidth, height: newHeight } : t
        );
        onUpdateTables(updatedTables);
    }
  };

  const handleMouseUp = () => {
    setDragState({ ...dragState, isDragging: false, type: null, itemId: null });
  };

  const handleCanvasClick = () => {
    if (!dragState.isDragging) {
      setSelectedId(null);
      setSelectionType(null);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
        setSelectedId(null);
        setSelectionType(null);
    }
    setIsEditing(!isEditing);
  };

  const addTable = (shape: TableShape) => {
    const categoryId = categories.length > 0 ? categories[0].id : 'SOCCER_SMALL';
    const catDef = categories.find(c => c.id === categoryId);
    
    const defaultCapacity = catDef?.defaultCapacity || 4;

    const newTable: Table = {
      id: crypto.randomUUID(),
      name: `T${tables.length + 1}`,
      capacity: defaultCapacity,
      shape: shape,
      category: categoryId,
      x: 100, 
      y: 100, 
      rotation: 0
    };

    const { width, height } = getRawTableSize(newTable);
    newTable.width = width;
    newTable.height = height;
    
    const centerX = (dimensions.width / 2) - (width / 2);
    const centerY = (dimensions.height / 2) - (height / 2);

    const maxX = dimensions.width - width;
    const maxY = dimensions.height - height;
    
    newTable.x = Math.round(Math.min(centerX, Math.max(0, maxX)) / 10) * 10;
    newTable.y = Math.round(Math.min(centerY, Math.max(0, maxY)) / 10) * 10;

    onUpdateTables([...tables, newTable]);
    setSelectedId(newTable.id);
    setSelectionType('TABLE');
  };

  const getGermanZoneName = (type: ZoneType) => {
    switch(type) {
      case ZoneType.SPORT_AREA: return 'Halle'; 
      case ZoneType.LOCKER_ROOM: return 'Umkleidekabine';
      case ZoneType.LOUNGE: return 'Bar';
      case ZoneType.OBSTACLE: return 'Wand/Säule';
      case ZoneType.CUSTOM: return 'Eigener Bereich';
      case ZoneType.ENTRANCE: return 'Eingang';
      default: return 'Bereich';
    }
  };

  const addZone = (type: ZoneType) => {
    let w = 200;
    let h = 200;

    if (type === ZoneType.OBSTACLE) {
        w = 120;
        h = 20;
    }

    const newZone: Zone = {
      id: crypto.randomUUID(),
      name: getGermanZoneName(type),
      type,
      x: 50, 
      y: 50, 
      width: w,
      height: h,
      customColor: type === ZoneType.CUSTOM ? '#e0f2fe' : undefined
    };

    const centerX = (dimensions.width / 2) - (w / 2);
    const centerY = (dimensions.height / 2) - (h / 2);

    newZone.x = Math.round(Math.min(centerX, dimensions.width - w) / 10) * 10;
    newZone.y = Math.round(Math.min(centerY, dimensions.height - h) / 10) * 10;

    onUpdateZones([...zones, newZone]);
    setSelectedId(newZone.id);
    setSelectionType('ZONE');
  };

  const deleteItem = () => {
    if (!selectedId) return;
    if (selectionType === 'TABLE') {
      onUpdateTables(tables.filter(t => t.id !== selectedId));
    } else {
      onUpdateZones(zones.filter(z => z.id !== selectedId));
    }
    setSelectedId(null);
    setSelectionType(null);
  };

  const updateSelectedTable = (key: keyof Table, value: any) => {
    if (selectionType !== 'TABLE' || !selectedId) return;
    
    if (key === 'rotation') {
       const table = tables.find(t => t.id === selectedId);
       if (!table) return;
       
       const newTable = { ...table, [key]: value };
       const { width, height } = getRawTableSize(newTable);
       const { boundingW, boundingH } = getRotatedBoundingBox(width, height, newTable.rotation);
       
       const diffW = (boundingW - width) / 2;
       const diffH = (boundingH - height) / 2;
       
       let newX = newTable.x;
       let newY = newTable.y;
       
       const minX = diffW;
       const maxX = dimensions.width - width - diffW;
       const minY = diffH;
       const maxY = dimensions.height - height - diffH;
       
       if (newX < minX) newX = minX;
       if (newX > maxX) newX = maxX;
       if (newY < minY) newY = minY;
       if (newY > maxY) newY = maxY;
       
       onUpdateTables(tables.map(t => t.id === selectedId ? { ...t, [key]: value, x: newX, y: newY } : t));
    } else if (key === 'category') {
        onUpdateTables(tables.map(t => t.id === selectedId ? { ...t, [key]: value } : t));
    } else {
       onUpdateTables(tables.map(t => t.id === selectedId ? { ...t, [key]: value } : t));
    }
  };

  const updateSelectedZone = (key: keyof Zone, value: any) => {
    if (selectionType !== 'ZONE' || !selectedId) return;
    
    if (key === 'width' || key === 'height') {
       const zone = zones.find(z => z.id === selectedId);
       if (!zone) return;
       
       const newVal = value as number;
       if (key === 'width' && zone.x + newVal > dimensions.width) return;
       if (key === 'height' && zone.y + newVal > dimensions.height) return;
    }

    onUpdateZones(zones.map(z => z.id === selectedId ? { ...z, [key]: value } : z));
  };

  const rotateTable = () => {
    if (selectionType !== 'TABLE' || !selectedId) return;
    const table = tables.find(t => t.id === selectedId);
    if (table) updateSelectedTable('rotation', (table.rotation + 45) % 360);
  };

  const selectedTable = selectionType === 'TABLE' ? tables.find(t => t.id === selectedId) : null;
  const selectedZone = selectionType === 'ZONE' ? zones.find(z => z.id === selectedId) : null;

  return (
    <div className="flex flex-col h-[calc(100vh-100px)]">
      
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 px-1 gap-3 md:gap-0">
          
          <div className="flex items-baseline gap-2">
             <h2 className="text-xl font-bold text-inherit whitespace-nowrap">
                {isEditing ? 'Layout bearbeiten' : 'Hallen-Überblick'}
             </h2>
             <span className="text-xs opacity-70 truncate">
                {isEditing ? 'Klicken und ziehen.' : 'Live-Status. Klicken für Details.'}
             </span>
          </div>

          <div className="flex items-center justify-between md:justify-end gap-3 w-full md:w-auto">
            {!isEditing && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-green-900/10 border border-green-500/20 rounded-full shrink-0">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                    <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider">Live</span>
                </div>
            )}
            
            <div className="flex items-center gap-2 ml-auto md:ml-0">
                {isEditing && onReset && (
                    <button 
                        onClick={onReset}
                        className={`p-2 rounded-lg text-sm font-bold shadow-sm transition-all border bg-white hover:bg-red-50 border-slate-200 text-red-600`}
                        title="Alles zurücksetzen"
                    >
                        <RotateCcw size={16} />
                    </button>
                )}
                <button 
                    onClick={handleEditToggle}
                    className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shadow-sm transition-all whitespace-nowrap ${
                        isEditing 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : (isSimple ? 'bg-slate-900 hover:bg-slate-800 text-white border border-slate-900' : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200')
                    }`}
                >
                    {isEditing ? <><Check size={16} /> Fertig</> : <><Pencil size={16} /> Layout bearbeiten</>}
                </button>
            </div>
          </div>
      </div>

      <div className="flex flex-col md:flex-row flex-1 gap-4 overflow-hidden">
        {isEditing && (
          <div className={`w-full md:w-72 shadow-lg rounded-lg p-0 flex flex-col overflow-hidden shrink-0 animate-fade-in bg-white order-2 md:order-1`}>
              <div className={`flex border-b border-slate-200`}>
              <button 
                  onClick={() => setActiveTab('LAYOUT')}
                  className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'LAYOUT' ? 'text-amber-600 border-b-2 border-amber-500 bg-amber-50/10' : 'opacity-60 hover:opacity-100'}`}
              >
                  <Layout size={16} /> Layout
              </button>
              <button 
                  onClick={() => setActiveTab('FURNITURE')}
                  className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'FURNITURE' ? 'text-amber-600 border-b-2 border-amber-500 bg-amber-50/10' : 'opacity-60 hover:opacity-100'}`}
              >
                  <LayoutGrid size={16} /> Plätze
              </button>
              </div>

              <div className="p-4 flex-1 overflow-y-auto">
              {activeTab === 'LAYOUT' && (
                  <div className="space-y-6">
                  <div className={`p-3 rounded border bg-slate-50 border-slate-200`}>
                      <h3 className="text-xs font-bold opacity-60 uppercase tracking-wider mb-2 flex items-center gap-2">
                          <Maximize size={12} /> Hallengröße
                      </h3>
                      <div className="flex gap-2">
                          <div className="flex-1">
                              <label className="block text-[10px] font-medium opacity-60 mb-1">Breite (px)</label>
                              <input 
                                  type="number" 
                                  step="100"
                                  value={dimensions.width} 
                                  onChange={e => onUpdateDimensions({...dimensions, width: Math.max(400, parseInt(e.target.value) || 0)})} 
                                  className="w-full text-center text-[10px] font-bold text-slate-700 outline-none border border-slate-300 rounded py-1 bg-white"
                              />
                          </div>
                          <div className="flex-1">
                              <label className="block text-[10px] font-medium opacity-60 mb-1">Höhe (px)</label>
                              <input 
                                  type="number" 
                                  step="100"
                                  value={dimensions.height} 
                                  onChange={e => onUpdateDimensions({...dimensions, height: Math.max(400, parseInt(e.target.value) || 0)})} 
                                  className="w-full text-center text-[10px] font-bold text-slate-700 outline-none border border-slate-300 rounded py-1 bg-white"
                              />
                          </div>
                      </div>
                  </div>

                  <div>
                      <h3 className="text-xs font-bold opacity-60 uppercase tracking-wider mb-3">Bereiche hinzufügen</h3>
                      <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => addZone(ZoneType.SPORT_AREA)} className={`p-2 border rounded text-xs hover:opacity-80 flex flex-col items-center border-slate-200 text-slate-700 bg-white hover:bg-slate-50`}>
                          <BoxSelect size={20} className="mb-1 opacity-50"/> Halle
                      </button>
                      <button onClick={() => addZone(ZoneType.LOCKER_ROOM)} className={`p-2 border rounded text-xs hover:opacity-80 flex flex-col items-center border-slate-200 text-slate-700 bg-white hover:bg-slate-50`}>
                          <div className="w-5 h-5 bg-slate-300 mb-1 rounded-sm"></div> Umkleide
                      </button>
                      <button onClick={() => addZone(ZoneType.LOUNGE)} className={`p-2 border rounded text-xs hover:opacity-80 flex flex-col items-center border-slate-200 text-slate-700 bg-white hover:bg-slate-50`}>
                          <div className="w-5 h-5 bg-orange-200 mb-1 rounded-sm"></div> Bar
                      </button>
                      <button onClick={() => addZone(ZoneType.OBSTACLE)} className={`p-2 border rounded text-xs hover:opacity-80 flex flex-col items-center border-slate-200 text-slate-700 bg-white hover:bg-slate-50`}>
                          <div className="w-5 h-5 bg-slate-800 mb-1 rounded-sm"></div> Wand/Säule
                      </button>
                      </div>
                  </div>
                  </div>
              )}

              {activeTab === 'FURNITURE' && (
                  <div className="space-y-6">
                  <div>
                      <h3 className="text-xs font-bold opacity-60 uppercase tracking-wider mb-3">Plätze hinzufügen</h3>
                      <div className="grid grid-cols-3 gap-2">
                      <button onClick={() => addTable(TableShape.RECTANGLE)} className="flex flex-col items-center justify-center p-3 border rounded hover:opacity-80 transition border-slate-200 hover:bg-slate-50">
                          <div className="w-8 h-8 border-2 border-slate-400 rounded-sm mb-1"></div>
                          <span className="text-[10px] opacity-70">Eckig</span>
                      </button>
                      <button onClick={() => addTable(TableShape.CIRCLE)} className="flex flex-col items-center justify-center p-3 border rounded hover:opacity-80 transition border-slate-200 hover:bg-slate-50">
                          <div className="w-8 h-8 border-2 border-slate-400 rounded-full mb-1"></div>
                          <span className="text-[10px] opacity-70">Rund</span>
                      </button>
                      <button onClick={() => addTable(TableShape.TRIANGLE)} className="flex flex-col items-center justify-center p-3 border rounded hover:opacity-80 transition border-slate-200 hover:bg-slate-50">
                          <Triangle size={24} className="text-slate-400 mb-1" />
                          <span className="text-[10px] opacity-70">Dreieck</span>
                      </button>
                      </div>
                  </div>
                  </div>
              )}
              </div>
          </div>
        )}

        {/* Canvas container with slightly tinted background to avoid white-flicker */}
        <div className={`flex-1 rounded-lg overflow-auto border relative order-1 md:order-2 p-20 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-100 border-slate-200'}`}>
          <div 
            className="relative shadow-lg transition-all duration-300 ease-in-out m-auto" 
            ref={canvasRef}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleCanvasClick}
            style={{ 
              width: dimensions.width,
              height: dimensions.height,
              minWidth: dimensions.width, 
              minHeight: dimensions.height, 
              backgroundImage: `radial-gradient(${isDarkMode ? '#334155' : '#cbd5e1'} 1px, transparent 1px)`, 
              backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
              backgroundSize: '20px 20px' 
            }}
          >
            {zones.map(zone => (
              <ZoneNode 
                key={zone.id}
                zone={zone}
                isSelected={selectedId === zone.id}
                isSimple={isSimple}
                onMouseDown={isEditing ? handleZoneMouseDown : undefined}
                onResizeStart={isEditing ? handleZoneResizeStart : undefined}
              />
            ))}

            {tables.map(table => {
              const tableReservations = reservations.filter(
                  r => r.tableId === table.id && 
                  r.date === new Date().toISOString().split('T')[0]
              );

              return (
                  <TableNode 
                  key={table.id}
                  table={table}
                  categories={categories}
                  isSelected={selectedId === table.id}
                  isSimple={isSimple}
                  reservations={tableReservations} 
                  onMouseDown={isEditing ? handleTableMouseDown : undefined}
                  onResizeStart={isEditing ? handleTableResizeStart : undefined}
                  onTableClick={onTableClick}
                  />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
